import React from 'react';
import { Heart, Pin, MoreVertical, Trash2, Edit, Image, Video, FileText, Link as LinkIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import DOMPurify from 'dompurify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const PostBubble = ({ 
  post, 
  onLike, 
  onEdit, 
  onDelete, 
  isOwner = false,
  wallName,
  wallProfileImage,
  showWallInfo = false
}) => {
  const getPostTypeIcon = (type) => {
    switch (type) {
      case 'photo': return <Image className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'file': return <FileText className="w-4 h-4" />;
      case 'link': return <LinkIcon className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="relative group mb-4">
      {/* Bubble Container */}
      <div className={cn(
        "relative bg-gradient-to-br from-[#1a1a2e]/90 to-[#16162a]/90",
        "backdrop-blur-xl rounded-2xl p-4 border border-[#6D4AFF]/20",
        "shadow-[0_0_20px_rgba(109,74,255,0.1)]",
        "hover:shadow-[0_0_30px_rgba(109,74,255,0.2)] transition-all duration-300",
        post.is_pinned && "border-yellow-500/50"
      )}>
        {/* Pinned indicator */}
        {post.is_pinned && (
          <div className="absolute -top-2 -right-2 bg-yellow-500 rounded-full p-1">
            <Pin className="w-3 h-3 text-black" />
          </div>
        )}

        {/* Wall info (for feed view) */}
        {showWallInfo && (
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#6D4AFF]/20">
            {wallProfileImage ? (
              <img 
                src={wallProfileImage} 
                alt={wallName} 
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#00bcd4] flex items-center justify-center text-white text-sm font-bold">
                {wallName?.charAt(0) || 'W'}
              </div>
            )}
            <span className="text-sm font-medium text-white">{wallName}</span>
          </div>
        )}

        {/* Post Type Badge */}
        {post.post_type !== 'text' && (
          <div className="flex items-center gap-1 text-xs text-[#6D4AFF] mb-2">
            {getPostTypeIcon(post.post_type)}
            <span className="capitalize">{post.post_type}</span>
          </div>
        )}

        {/* Content */}
        {post.content && (
          <div 
            className="text-gray-200 text-sm leading-relaxed mb-3"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content, { ALLOWED_TAGS: ['b','i','em','strong','a','br','p','ul','ol','li','h1','h2','h3','img','blockquote','code','pre','span'], ALLOWED_ATTR: ['href','src','alt','class','target','rel'] }) }}
          />
        )}

        {/* Media */}
        {post.media_urls && post.media_urls.length > 0 && (
          <div className={cn(
            "grid gap-2 mb-3",
            post.media_urls.length === 1 && "grid-cols-1",
            post.media_urls.length === 2 && "grid-cols-2",
            post.media_urls.length >= 3 && "grid-cols-2"
          )}>
            {post.media_urls.map((url, idx) => (
              <div key={idx} className="relative rounded-xl overflow-hidden">
                {post.post_type === 'video' ? (
                  <video 
                    src={url} 
                    controls 
                    className="w-full max-h-64 object-cover"
                  />
                ) : (
                  <img 
                    src={url} 
                    alt="" 
                    className="w-full max-h-64 object-cover hover:scale-105 transition-transform cursor-pointer"
                    onClick={() => window.open(url, '_blank')}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Link Preview */}
        {post.link_url && post.link_preview && (
          <a 
            href={post.link_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="block bg-[#252540] rounded-xl p-3 mb-3 hover:bg-[#2d2d50] transition-colors"
          >
            {post.link_preview.image && (
              <img 
                src={post.link_preview.image} 
                alt="" 
                className="w-full h-32 object-cover rounded-lg mb-2"
              />
            )}
            <p className="text-sm font-medium text-white truncate">
              {post.link_preview.title || post.link_url}
            </p>
            {post.link_preview.description && (
              <p className="text-xs text-gray-400 truncate">
                {post.link_preview.description}
              </p>
            )}
          </a>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-[#6D4AFF]/10">
          <div className="flex items-center gap-4">
            {/* Like button */}
            <button 
              onClick={() => onLike?.(post.id)}
              className={cn(
                "flex items-center gap-1.5 text-sm transition-all",
                post.is_liked 
                  ? "text-pink-500" 
                  : "text-gray-400 hover:text-pink-400"
              )}
            >
              <Heart 
                className={cn(
                  "w-4 h-4 transition-all",
                  post.is_liked && "fill-current scale-110"
                )} 
              />
              <span>{post.like_count || 0}</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </span>

            {/* Owner actions */}
            {isOwner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1a1a2e] border-[#2d2d44]">
                  <DropdownMenuItem 
                    onClick={() => onEdit?.(post)}
                    className="text-gray-300 hover:text-white focus:bg-[#252540]"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onDelete?.(post.id)}
                    className="text-red-400 hover:text-red-300 focus:bg-[#252540]"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Glow effect */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#6D4AFF]/5 to-[#00bcd4]/5 blur-xl rounded-2xl" />
    </div>
  );
};

export default PostBubble;
